import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Button, Column, Icon, Row, Select, TextArea, TextInput } from '@pella/sash-framework';
import { isValidEmail, isValidPhoneNumber, isValidPostal } from '../../../utilities/validations';
import { eloquaTrackingUrl } from '../../../utilities/constants';
import { HandleEloquaError } from '../../../../hooks/utils/eloquaError';
import EloquaForm from '../../eloqua-form';
import { useLocation } from '../../../../context/LocationContext';

const SteadySetQuote = ({ path, className }) => {
	const { setLocation } = useLocation();
	const [values, setValues] = useState({});
	const [errors, setErrors] = useState({});
	const [disabled, setDisabled] = useState(false);
	const [success, setSuccess] = useState(false);
	const [referralUrl, setReferralUrl] = useState(path);
	const [utms, setUtms] = useState({});

	useEffect(() => {
		let params = new URLSearchParams(window.location.search);
		let utm_campaign = params.get('utm_campaign');
		let utm_source = params.get('utm_source');
		let utm_medium = params.get('utm_medium');
		let utm_content = params.get('utm_content');
		let utm_term = params.get('utm_term');
		let referral = params.get('referral');

		setUtms({ utm_campaign, utm_source, utm_medium, utm_content, utm_term });
		if (referral) setReferralUrl(referral || path);
	}, [path]);

	const errorMessages = {
		firstName: 'Please enter your first name',
		lastName: 'Please enter your last name',
		emailAddress: 'Please enter a valid email address',
		company: 'Please enter a company name',
		businesstype: 'Please select a primary business type',
		mobilePhone: 'Please enter a valid phone number',
		zipPostal: 'Please enter a valid postal code',
	};

	const onInputChange = (event) => {
		const { checked, name, type, value } = event.target;

		let savedValue = value;
		if (type === 'checkbox') savedValue = checked;

		setValues({ ...values, [name]: savedValue });

		if (name !== 'emailAddress') validateField(name, savedValue);
		if (disabled) setDisabled(false);
		if (success) setSuccess(false);
	};

	const onInputBlur = (event) => {
		const { name, value } = event.target;
		validateField(name, value);
	};

	const validateField = (name, value) => {
		let error = '';

		if (!value && errorMessages[name]) {
			error = errorMessages[name];
		} else if (name === 'emailAddress' && !isValidEmail(value)) {
			error = errorMessages.emailAddress;
		} else if (name === 'mobilePhone' && !isValidPhoneNumber(value)) {
			error = errorMessages.mobilePhone;
		}

		setErrors({ ...errors, [name]: error });
	};

	const validateForm = () => {
		let errors = [];

		if (!values.firstName) {
			errors.firstName = errorMessages.firstName;
		}

		if (!values.lastName) {
			errors.lastName = errorMessages.lastName;
		}

		if (!isValidEmail(values.emailAddress)) {
			errors.emailAddress = errorMessages.emailAddress;
		}

		if (!values.company) {
			errors.company = errorMessages.company;
		}

		if (!values.businesstype) {
			errors.businesstype = errorMessages.businesstype;
		}

		if (!isValidPhoneNumber(values.mobilePhone)) {
			errors.mobilePhone = errorMessages.mobilePhone;
		}

		if (!isValidPostal(values.zipPostal)) {
			errors.zipPostal = errorMessages.zipPostal;
		}

		if (Object.keys(errors).length > 0) {
			setErrors(errors);
			return false;
		}

		return true;
	};

	const onFormSubmit = (e) => {
		e.preventDefault();

		if (!validateForm()) return;

		const data = new FormData(e.target);
		setErrors({});
		setDisabled(true);
		setLocation(values.zipPostal);

		axios
			.post(eloquaTrackingUrl, data)
			.then(() => {
				setDisabled(false);
				setSuccess(true);
			})
			.catch((error) => {
				HandleEloquaError(error);
				setDisabled(false);
			});
	};

	return (
		<div className='bg-white p-4 md:py-6 xl:px-6'>
			{!success && (
				<EloquaForm
					elqFormName='SteadySet-GetaQuote'
					onFormSubmit={onFormSubmit}
					elqCookieWrite={1}
					className={className || ''}
					utm={utms}
				>
					<input
						type='hidden'
						name='url'
						value={referralUrl}
					/>

					<Row>
						<Column
							xs={12}
							md={6}
						>
							<TextInput
								id='firstName'
								labelText='First Name'
								onChange={onInputChange}
								required={true}
								error={errors.firstName}
								errorText={errors.firstName}
								autoComplete='given-name'
							/>
						</Column>

						<Column
							xs={12}
							md={6}
						>
							<TextInput
								id='lastName'
								labelText='Last Name'
								onChange={onInputChange}
								required={true}
								error={errors.lastName}
								errorText={errors.lastName}
								autoComplete='family-name'
							/>
						</Column>

						<Column
							xs={12}
							md={6}
						>
							<TextInput
								id='emailAddress'
								labelText='Email Address'
								onChange={onInputChange}
								onBlur={onInputBlur}
								required={true}
								error={errors.emailAddress}
								errorText={errors.emailAddress}
								autoComplete='email'
							/>
						</Column>

						<Column
							xs={12}
							md={6}
						>
							<TextInput
								id='company'
								labelText='Company Name'
								onChange={onInputChange}
								required={true}
								error={errors.company}
								errorText={errors.company}
								autoComplete='organization'
							/>
						</Column>

						<Column xs={12}>
							<Select
								id='businesstype'
								labelText='Primary Business Type'
								onChange={onInputChange}
								required={true}
								error={errors.businesstype}
								errorText={errors.businesstype}
								className={!values.businesstype ? 'text-neutral-400 focus:text-black' : ''}
								autoComplete='off'
							>
								<option value=''>-- Select an option --</option>
								<option value='Replacement Contractor'>Replacement Contractor</option>
								<option value='Remodeling Contractor'>Remodeling Contractor</option>
								<option value='Residential Builder/GC'>Residential Builder/GC</option>
								<option value='Commercial Builder/GC'>Commercial Builder/GC</option>
								<option value='Architectural/Design Firm'>Architectural / Design Firm</option>
								<option value='Commercial Building Owner'>Commercial Building Owner</option>
								<option value='Homeowner'>Homeowner</option>
							</Select>
						</Column>

						<Column xs={6}>
							<TextInput
								type='tel'
								id='mobilePhone'
								labelText='Mobile Phone'
								onChange={onInputChange}
								format='(###) ###-####'
								required
								allowEmptyFormatting
								error={errors.mobilePhone}
								errorText={errors.mobilePhone}
								autoComplete='tel-national'
							/>
						</Column>

						<Column xs={6}>
							<TextInput
								id='zipPostal'
								labelText='Project Zip Code'
								onChange={onInputChange}
								required={true}
								error={errors.zipPostal}
								errorText={errors.zipPostal}
								autoComplete='postal-code'
							/>
						</Column>

						<Column xs={12}>
							<Select
								id='preferredchannel'
								labelText='Preferred Way to Purchase Pella Products'
								onChange={onInputChange}
								className={!values.preferredchannel ? 'text-neutral-400 focus:text-black' : ''}
								autoComplete='off'
							>
								<option value=''>-- Select an option --</option>
								<option value='Local Pella Rep'>Local Pella Rep</option>
								<option value='Lumberyard'>Lumberyard</option>
								<option value="Lowe's">Lowe&apos;s</option>
								<option value='No Preference'>No Preference</option>
							</Select>
						</Column>

						<Column xs={12}>
							<TextArea
								id='details'
								labelText='Additional Details'
								onChange={onInputChange}
								autoComplete='off'
							/>
						</Column>

						<Column xs={12}>
							<Button
								color='primary-alt'
								type='submit'
								wide={true}
								disabled={disabled}
							>
								{disabled ? 'Processing...' : 'Get A Quote'}
							</Button>
						</Column>
					</Row>
				</EloquaForm>
			)}

			{success && (
				<div className='md:py-10'>
					<h3 className='mb-3 flex flex-row items-center gap-2'>
						<Icon
							name='check_circle'
							size={32}
							className='text-green'
						/>
						Thanks for requesting a project quote!
					</h3>
					<p className='text-subtitle'>We have received your submission and will get in touch soon.</p>
				</div>
			)}
		</div>
	);
};

SteadySetQuote.propTypes = {
	path: PropTypes.string,
	className: PropTypes.string,
};

export default SteadySetQuote;
